import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, RefreshCw, Image as ImageIcon, Award, Trash2, Download, Save, Upload, X, Star } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

// Habbo-Badge-CDN
const badgeUrl = (code) => `https://images.habbo.com/c_images/album1584/${code}.gif`;

const PromoDesignerPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  // Promo-Auswahl
  const [promos, setPromos] = useState([]);
  const [promoCount, setPromoCount] = useState(0);
  const [favoritesTotal, setFavoritesTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // promo url
  const [refreshing, setRefreshing] = useState(false);

  // Editor-State
  const [overlays, setOverlays] = useState([]); // { id, type:'badge'|'image', src, x, y, w, h }
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [badgeCodeInput, setBadgeCodeInput] = useState("");
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const canvasWrapRef = useRef(null);
  const bgImgRef = useRef(null);

  // Promo-URL via Backend-Proxy (umgeht Hotlink-Protection + Same-Origin fuer Canvas)
  const promoUrl = (name) => `${apiBase}/api/web-promos/proxy?name=${encodeURIComponent(name)}`;

  const loadPromos = async (newSkip = 0, query = q, fav = onlyFavorites) => {
    setLoading(true);
    try {
      const r = await a.listWebPromos({ q: query, skip: newSkip, limit: 36, only_favorites: fav });
      // URLs durch Proxy-URLs ersetzen
      const proxied = (r.items || []).map((it) => ({ ...it, url: promoUrl(it.name) }));
      setPromos(proxied);
      setPromoCount(r.total || 0);
      setFavoritesTotal(r.favorites_total || 0);
      setSkip(newSkip);
    } catch (e) {
      toast({ title: "Promos laden fehlgeschlagen", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const toggleFavorite = async (name, current) => {
    try {
      await a.toggleWebPromoFavorite(name, !current);
      setPromos((prev) => prev.map((p) => p.name === name ? { ...p, favorite: !current } : p));
      setFavoritesTotal((n) => n + (current ? -1 : 1));
    } catch {
      toast({ title: "Favorit konnte nicht gespeichert werden", variant: "destructive" });
    }
  };

  const loadMedia = async () => {
    try { const r = await a.listUploads(); setMedia(r.items || []); }
    catch (e) { /* silent */ }
  };

  useEffect(() => { loadPromos(); loadMedia(); }, []);

  // Auto-Overlay aus Furni-Tool: wenn ein Furni "Im Designer oeffnen" geklickt
  // wurde, liegt unter sessionStorage["promo_pending_overlay"] = {url,name}.
  // Sobald ein Promo-Background geladen ist, wird das Furni automatisch
  // als Overlay platziert.
  useEffect(() => {
    if (!selected) return;
    let raw;
    try { raw = sessionStorage.getItem("promo_pending_overlay"); } catch { return; }
    if (!raw) return;
    try {
      const pending = JSON.parse(raw);
      const fullUrl = pending.url?.startsWith("http") ? pending.url : `${apiBase}${pending.url}`;
      loadImageDimensions(fullUrl).then(({ w, h }) => {
        setOverlays((prev) => [
          ...prev,
          { id: Date.now() + Math.random(), type: "image", src: fullUrl, x: 30, y: 30, w, h, naturalW: w, naturalH: h },
        ]);
        toast({ title: "Moebel platziert", description: pending.name || "Furni als Overlay hinzugefuegt" });
      });
      sessionStorage.removeItem("promo_pending_overlay");
    } catch { /* ignore */ }
  }, [selected]);

  const onSearch = (e) => { e?.preventDefault?.(); loadPromos(0, q); };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await a.refreshWebPromos();
      toast({ title: `Refresh komplett`, description: `Total: ${r.total}, neu: ${r.new}, ${r.duration_s}s` });
      await loadPromos(0, q);
    } catch (e) {
      toast({ title: "Refresh fehlgeschlagen", variant: "destructive" });
    } finally { setRefreshing(false); }
  };

  // Laedt ein Bild und liefert die echte Pixelgroesse zurueck. Behaelt das
  // native Seitenverhaeltnis bei und skaliert ggf. auf max 160px lange Seite,
  // damit Badges/Furnis nie verzerrt platziert werden.
  const MAX_OVERLAY_SIDE = 160;
  const loadImageDimensions = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const nw = img.naturalWidth || 64;
      const nh = img.naturalHeight || 64;
      const longest = Math.max(nw, nh);
      const factor = longest > MAX_OVERLAY_SIDE ? MAX_OVERLAY_SIDE / longest : 1;
      resolve({ w: Math.round(nw * factor), h: Math.round(nh * factor), nw, nh });
    };
    img.onerror = () => resolve({ w: 64, h: 64, nw: 64, nh: 64 });
    img.src = src;
  });

  // Editor: Overlay hinzufuegen
  const addBadge = async () => {
    const code = badgeCodeInput.trim().toUpperCase();
    if (!code) return;
    const src = badgeUrl(code);
    const { w, h, nw, nh } = await loadImageDimensions(src);
    setOverlays((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), type: "badge", src, code, x: 20, y: 20, w, h, naturalW: nw, naturalH: nh },
    ]);
    setBadgeCodeInput("");
  };

  const addImage = async (url) => {
    const { w, h, nw, nh } = await loadImageDimensions(url);
    setOverlays((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), type: "image", src: url, x: 30, y: 30, w, h, naturalW: nw, naturalH: nh },
    ]);
  };

  const removeOverlay = (id) => setOverlays((p) => p.filter((o) => o.id !== id));
  const clearOverlays = () => setOverlays([]);

  // Drag-Logik (mouse + touch)
  const startDrag = (e, overlay) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveOverlay(overlay.id);
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    const origX = overlay.x;
    const origY = overlay.y;
    const onMove = (ev) => {
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (cx == null) return;
      const dx = cx - startX;
      const dy = cy - startY;
      setOverlays((prev) => prev.map((o) => o.id === overlay.id ? { ...o, x: Math.max(0, origX + dx), y: Math.max(0, origY + dy) } : o));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  const resize = (id, deltaW) => {
    // Aspect-Ratio-preserving Resize: deltaW veraendert nur die Breite,
    // die Hoehe wird proportional ueber naturalW/naturalH neu berechnet.
    setOverlays((p) => p.map((o) => {
      if (o.id !== id) return o;
      const aspect = (o.naturalH && o.naturalW) ? (o.naturalH / o.naturalW) : (o.h / o.w);
      const newW = Math.max(16, o.w + deltaW);
      return { ...o, w: newW, h: Math.max(16, Math.round(newW * aspect)) };
    }));
  };

  // Render zu Canvas + Output
  const renderToCanvas = async () => {
    if (!selected || !bgImgRef.current) throw new Error("Kein Promo geladen");
    const img = bgImgRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false; // pixel-art
    ctx.drawImage(img, 0, 0);

    // Scale-Faktor: dom -> natural
    const domW = img.clientWidth;
    const scale = img.naturalWidth / domW;

    for (const o of overlays) {
      try {
        const oimg = await loadImg(o.src);
        ctx.drawImage(oimg, o.x * scale, o.y * scale, o.w * scale, o.h * scale);
      } catch (e) { /* skip on error */ }
    }
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
  };

  const loadImg = (src) => new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = src;
  });

  const saveAs = async (mode) => {
    if (!selected) return;
    setSaving(true);
    try {
      const blob = await renderToCanvas();
      if (mode === "download") {
        const url = URL.createObjectURL(blob);
        const a2 = document.createElement("a");
        a2.href = url;
        a2.download = `habthek-promo-${Date.now()}.png`;
        a2.click();
        URL.revokeObjectURL(url);
        toast({ title: "Heruntergeladen" });
      } else if (mode === "library") {
        const fd = new FormData();
        fd.append("files", blob, `promo-${Date.now()}.png`);
        const r = await a.uploadImages(fd);
        await loadMedia();
        toast({ title: "In Medien-Bibliothek gespeichert", description: r.urls?.[0] });
      } else if (mode === "copy-url") {
        const fd = new FormData();
        fd.append("files", blob, `promo-${Date.now()}.png`);
        const r = await a.uploadImages(fd);
        await navigator.clipboard.writeText(`${apiBase}${r.urls[0]}`);
        toast({ title: "URL kopiert", description: "Direkt in Artikel als Header einsetzen" });
      }
    } catch (err) {
      toast({ title: "Export fehlgeschlagen", description: String(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  // ---------- UI ----------
  if (selected) {
    return (
      <div className="space-y-4" data-testid="promo-designer">
        <div className="flex items-center justify-between">
          <Button onClick={() => { setSelected(null); clearOverlays(); }} variant="ghost" className="text-zinc-300 hover:text-white" data-testid="promo-back">
            <X className="mr-1 h-4 w-4" /> Zurueck zur Auswahl
          </Button>
          <div className="flex gap-2">
            <Button onClick={() => saveAs("download")} disabled={saving} className="btn-primary" data-testid="promo-export-download">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Download
            </Button>
            <Button onClick={() => saveAs("library")} disabled={saving} className="btn-primary" data-testid="promo-export-library">
              <Save className="mr-1 h-4 w-4" /> In Medien speichern
            </Button>
            <Button onClick={() => saveAs("copy-url")} disabled={saving} className="btn-primary" data-testid="promo-export-copy">
              <Upload className="mr-1 h-4 w-4" /> Hochladen + URL kopieren
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
          {/* Canvas-Bereich */}
          <div ref={canvasWrapRef} className="relative inline-block rounded-xl border border-white/10 bg-[#0a0d12] p-2 overflow-auto" data-testid="promo-canvas">
            <div className="relative inline-block">
              <img
                ref={bgImgRef}
                src={selected}
                alt="Promo"
                crossOrigin="anonymous"
                className="block max-w-full"
                style={{ imageRendering: "pixelated" }}
              />
              {overlays.map((o) => (
                <div
                  key={o.id}
                  className={`absolute group cursor-move ${activeOverlay === o.id ? "ring-2 ring-[#7c4dff]" : "ring-1 ring-white/20 hover:ring-white/60"}`}
                  style={{ left: `${o.x}px`, top: `${o.y}px`, width: `${o.w}px`, height: `${o.h}px` }}
                  onMouseDown={(e) => startDrag(e, o)}
                  onTouchStart={(e) => startDrag(e, o)}
                  onClick={(e) => { e.stopPropagation(); setActiveOverlay(o.id); }}
                  data-testid={`overlay-${o.id}`}
                >
                  <img src={o.src} alt="" className="h-full w-full object-contain pointer-events-none" style={{ imageRendering: "pixelated" }} />
                  {activeOverlay === o.id && (
                    <>
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeOverlay(o.id); }}
                        className="absolute -right-2 -top-2 rounded-full bg-rose-600 p-0.5 text-white shadow"
                        data-testid={`overlay-remove-${o.id}`}>
                        <X className="h-3 w-3" />
                      </button>
                      <div className="absolute -bottom-7 left-0 flex gap-1 text-[10px]">
                        <button type="button" onClick={(e) => { e.stopPropagation(); resize(o.id, -10); }} className="rounded bg-black/70 px-1.5 text-white">−</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); resize(o.id, 10); }} className="rounded bg-black/70 px-1.5 text-white">＋</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar: Badge + Medien */}
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-[#0f141b] p-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500">Badge hinzufuegen</div>
              <div className="mt-2 flex gap-1">
                <Input value={badgeCodeInput} onChange={(e) => setBadgeCodeInput(e.target.value)}
                  placeholder="z.B. HW_T1" className="bg-[#10141b] border-white/10 text-zinc-200 text-xs" data-testid="badge-code-input" />
                <Button onClick={addBadge} className="btn-primary px-3 text-xs" data-testid="add-badge"><Award className="h-3 w-3" /></Button>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0f141b] p-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Medien-Bibliothek</div>
              <div className="max-h-[300px] overflow-y-auto grid grid-cols-3 gap-1">
                {media.slice(0, 60).map((m) => (
                  <button key={m.name} type="button" onClick={() => addImage(`${apiBase}${m.url}`)}
                    className="aspect-square overflow-hidden rounded border border-white/10 bg-[#10141b] hover:border-[#7c4dff]"
                    title={m.name}>
                    <img src={`${apiBase}${m.url}`} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0f141b] p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">{overlays.length} Overlay{overlays.length === 1 ? "" : "s"}</span>
                {overlays.length > 0 && (
                  <button onClick={clearOverlays} className="text-rose-400 hover:text-rose-300" data-testid="clear-overlays">
                    <Trash2 className="inline h-3 w-3" /> Alle entfernen
                  </button>
                )}
              </div>
              <p className="mt-2 text-[10px] text-zinc-500">Klick auf Overlay zum Auswaehlen. Ziehen zum Verschieben. ＋/− zum Skalieren.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Promo-Auswahl-Grid
  const totalPages = Math.ceil(promoCount / 36);
  const currentPage = Math.floor(skip / 36) + 1;
  const pendingFurni = (() => {
    try { return JSON.parse(sessionStorage.getItem("promo_pending_overlay") || "null"); }
    catch { return null; }
  })();

  return (
    <div className="space-y-4" data-testid="promo-designer">
      {pendingFurni && (
        <div className="rounded-xl border border-[#7c4dff]/50 bg-[#1a1330] p-3 text-sm text-[#d4c7ff] flex items-center gap-3" data-testid="promo-pending-furni-hint">
          <img src={pendingFurni.url?.startsWith("http") ? pendingFurni.url : `${apiBase}${pendingFurni.url}`}
            alt="" className="h-10 w-10 object-contain" style={{ imageRendering: "pixelated" }} />
          <span><b>{pendingFurni.name}</b> wartet auf einen Hintergrund – waehle unten ein Promo und das Moebel landet automatisch drauf.</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Promo-Designer</h2>
          <p className="text-xs text-zinc-400 mt-1">
            {promoCount.toLocaleString("de-DE")} grosse Habbo-Webpromos · Cache aktualisiert sich alle 20 Minuten automatisch
          </p>
        </div>
        <Button onClick={onRefresh} variant="ghost" className="text-zinc-300 hover:text-white" disabled={refreshing} data-testid="promo-refresh">
          <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Aktualisiert..." : "Jetzt aktualisieren"}
        </Button>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Promo suchen (z.B. halloween, summer, hc) …"
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200" data-testid="promo-search" />
        </div>
        <Button type="submit" className="btn-primary">Suchen</Button>
        <Button
          type="button"
          onClick={() => { const nv = !onlyFavorites; setOnlyFavorites(nv); loadPromos(0, q, nv); }}
          variant={onlyFavorites ? "default" : "ghost"}
          className={onlyFavorites ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30" : "text-zinc-300 hover:text-white"}
          data-testid="promo-toggle-favorites"
          title={onlyFavorites ? "Alle anzeigen" : `Nur Favoriten (${favoritesTotal})`}
        >
          <Star className={`mr-1 h-4 w-4 ${onlyFavorites ? "fill-amber-400 text-amber-400" : ""}`} />
          Favoriten ({favoritesTotal})
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : promos.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] py-16 text-center text-sm text-zinc-500">
          <ImageIcon className="mx-auto h-10 w-10 text-zinc-700 mb-2" />
          {onlyFavorites ? "Noch keine Favoriten – setze auf den Tiles unten einen Stern." : "Keine Promos. Erste Crawl-Runde laeuft im Hintergrund (~30s)."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="promo-grid">
            {promos.map((p) => (
              <div key={p.name}
                className={`group relative overflow-hidden rounded-lg border ${p.favorite ? "border-amber-500/60" : p.is_new ? "border-emerald-500/40" : "border-white/10"} hover:border-[#7c4dff] bg-[#0f141b] transition-all hover:-translate-y-0.5`}
                data-testid={`promo-tile-${p.name}`}>
                {p.is_new && (
                  <span
                    className="pointer-events-none absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 rounded-md border border-emerald-400/60 bg-gradient-to-br from-emerald-500 to-teal-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/40"
                    title={`Neu hinzugefuegt: ${p.added_at || ""}`}
                    data-testid={`promo-new-${p.name}`}
                  >
                    Neu
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(p.name, p.favorite); }}
                  className={`absolute top-1.5 right-1.5 z-10 rounded-full p-1.5 backdrop-blur-sm border ${p.favorite ? "bg-amber-500/30 border-amber-400/60" : "bg-black/60 border-white/10 opacity-0 group-hover:opacity-100"} transition-opacity`}
                  title={p.favorite ? "Favorit entfernen" : "Als Favorit speichern"}
                  data-testid={`promo-fav-${p.name}`}
                >
                  <Star className={`h-3.5 w-3.5 ${p.favorite ? "fill-amber-300 text-amber-300" : "text-white"}`} />
                </button>
                <button type="button" onClick={() => setSelected(p.url)} className="block w-full text-left">
                  <div className="aspect-[16/9] bg-[#0a0d12]">
                    <img src={p.url} alt={p.name} loading="lazy" className="h-full w-full object-contain" style={{ imageRendering: "pixelated" }} />
                  </div>
                  <div className="border-t border-white/10 p-2 text-[10px] font-mono text-zinc-400 truncate" title={p.name}>{p.name}</div>
                </button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-3" data-testid="promo-pagination">
              <Button onClick={() => loadPromos(0)} disabled={skip === 0} variant="ghost" className="text-zinc-300 px-2" title="Erste Seite" data-testid="promo-page-first">«« </Button>
              <Button onClick={() => loadPromos(Math.max(0, skip - 36))} disabled={skip === 0} variant="ghost" className="text-zinc-300 px-2" data-testid="promo-page-prev">«</Button>
              <select
                value={currentPage}
                onChange={(e) => {
                  const target = parseInt(e.target.value, 10);
                  loadPromos((target - 1) * 36);
                }}
                className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1 text-xs text-zinc-200 min-w-[100px]"
                data-testid="promo-page-select"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>Seite {p} / {totalPages}</option>
                ))}
              </select>
              <Button onClick={() => loadPromos(skip + 36)} disabled={skip + 36 >= promoCount} variant="ghost" className="text-zinc-300 px-2" data-testid="promo-page-next">»</Button>
              <Button onClick={() => loadPromos((totalPages - 1) * 36)} disabled={skip + 36 >= promoCount} variant="ghost" className="text-zinc-300 px-2" title="Letzte Seite" data-testid="promo-page-last">»»</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PromoDesignerPanel;
